/**
 * Progressive-enhancement tabs over a product's description, collapsible_tab,
 * and app-block (e.g. reviews) panels. Markup stays valid, accordion-based
 * content with no JS at all — this only reorganizes it into a WAI-ARIA
 * tablist once it can run, and lets in-page links (e.g. the rating summary)
 * jump to and select the right tab.
 */
class ProductTabs extends HTMLElement {
  #panels = [];
  #tabs = [];
  #tablist = null;

  connectedCallback() {
    this.#panels = [
      ...this.querySelectorAll(
        ':scope > .product__description, :scope > .product__accordion, :scope > [data-product-tab]'
      ),
    ];
    if (this.#panels.length < 2) return;

    this.#buildTablist();
    this.#selectPanel(0, { focus: false });
    this.addEventListener('click', this.#handleJumpLinkClick);
  }

  #buildTablist() {
    this.#tablist = document.createElement('div');
    this.#tablist.className = 'product-tabs__list';
    this.#tablist.setAttribute('role', 'tablist');
    this.#tablist.setAttribute('aria-label', window.accessibilityStrings?.productTabs || 'Product information');
    this.#tablist.addEventListener('keydown', this.#handleKeydown);

    this.#panels.forEach((panel, index) => {
      const details = panel.querySelector('details');
      if (details) details.open = true;

      const label =
        panel.dataset.tabLabel ||
        panel.querySelector('.accordion__title')?.textContent.trim() ||
        `Tab ${index + 1}`;

      const tabId = panel.id ? `${panel.id}-tab` : `ProductTab-${index}`;
      if (!panel.id) panel.id = `ProductTabPanel-${index}`;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'product-tabs__tab';
      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.tabIndex = -1;
      tab.textContent = label;
      tab.addEventListener('click', () => this.#selectPanel(index, { focus: true }));

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.tabIndex = 0;

      this.#tablist.appendChild(tab);
      this.#tabs.push(tab);
      panel.classList.add('product-tabs__panel');
    });

    this.insertBefore(this.#tablist, this.#panels[0]);
  }

  /**
   * @param {number} activeIndex
   * @param {{ focus?: boolean }} options
   */
  #selectPanel(activeIndex, { focus = true } = {}) {
    this.#panels.forEach((panel, index) => {
      panel.hidden = index !== activeIndex;
    });

    this.#tabs.forEach((tab, index) => {
      const active = index === activeIndex;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });
  }

  #handleKeydown = (event) => {
    const currentIndex = this.#tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#selectPanel(nextIndex, { focus: true });
  };

  #handleJumpLinkClick = (event) => {
    const anchor = event.target.closest('a[href^="#"]');
    if (!anchor) return;

    const targetId = anchor.getAttribute('href').slice(1);
    const index = this.#panels.findIndex((panel) => panel.id === targetId);
    if (index === -1) return;

    this.#selectPanel(index, { focus: false });
  };
}

customElements.define('product-tabs', ProductTabs);
